import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Empty,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_NODE_API_ENDPOINT } from '../utils';
import { toast } from '../common/Popover';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useAuthOrg } from '../context/AuthOrgContext';
import { useMetaSetup } from '../context/MetaSetupContext';
import CommonLayout from '../common/LayoutBlock';
import {
    BotDetails,
    BotMilestones,
    GetRetargetingResponse,
    RetargetingData,
    WhatsAppTemplate,
} from '../interfaces/retargetting';
import '../assets/css/tablesShadow.css';

const { Text, Title } = Typography;

type PolicyId = number | string;
type DelayUnit = 'hours' | 'days';
type RetryFrequency = 'hour' | 'daily' | 'weekly';
type SecondaryCondition = 'reached' | 'not_reached';

interface ClientData {
    clientId: string;
    clientSubdomain: string;
    clientWaRegisteredNumber: string;
    phoneNumerId: string;
    wabaAccountId: string;
}

interface ClientBot {
    id: string;
    name: string;
    isActive: boolean;
}

interface MilestoneOption {
    id: string;
    name: string;
}

interface PolicyRuleDraft {
    id?: PolicyId;
    policyId?: PolicyId;
    initialMilestone: string | null;
    hasSecondaryCondition: boolean;
    secondaryMilestone: string | null;
    secondaryMilestoneCondition: SecondaryCondition | null;
    delayDuration: number;
    delayUnit: DelayUnit;
    messageTemplate: string | null;
    messageTemplateId: string | null;
    maxRetries: number;
    retryFrequency: RetryFrequency;
    messageConstants: Record<string, string>;
    messageJson: unknown;
}

interface NormalizedPolicy {
    id: PolicyId;
    name: string;
    isActive: boolean;
    isSelected: boolean;
    botId: string | null;
    botName: string | null;
    botStatus: boolean;
    clientId: string;
    orgId?: string;
    createdAt?: string | Date;
    updatedAt?: string | Date;
    rules: PolicyRuleDraft[];
}

interface ClientWithBot extends ClientData {
    bot: ClientBot | null;
    milestones: MilestoneOption[];
    templates: WhatsAppTemplate[];
    policies: NormalizedPolicy[];
}

interface VariableEditorState {
    open: boolean;
    ruleIndex: number | null;
    values: Record<string, string>;
}

const CLIENT_PAGE_SIZE = 9;
const POLICY_PAGE_SIZE = 10;
const DEFAULT_MESSAGE_CONSTANTS: Record<string, string> = { user_name: '{{user_name}}' };

const delayUnitOptions = [
    { label: 'Hours', value: 'hours' },
    { label: 'Days', value: 'days' },
];

const retryFrequencyOptions = [
    { label: 'Hourly', value: 'hour' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
];

const secondaryConditionOptions = [
    { label: 'Reached', value: 'reached' },
    { label: 'Not reached', value: 'not_reached' },
];

const normalizeBoolean = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1 || value === 200;
    if (typeof value === 'string') {
        const normalized = value.toLowerCase().trim();
        return normalized === 'true' || normalized === '1' || normalized === '200';
    }
    return false;
};

const safeJsonParse = <T,>(value: unknown, fallback: T): T => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value !== 'string') return value as T;

    try {
        return JSON.parse(value) as T;
    } catch {
        return fallback;
    }
};

const normalizeVariableMap = (
    value: unknown,
    fallback: Record<string, string> = {}
): Record<string, string> => {
    const parsed = safeJsonParse<Record<string, unknown>>(value, fallback);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { ...fallback };
    }

    return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, item]) => {
        acc[key] = item === null || item === undefined ? '' : String(item);
        return acc;
    }, {});
};

const formatTemplateVariable = (value: string): string =>
    value.replace(/_(\d+)$/, '_{{$1}}');

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const applyTemplateVariables = (
    messageJson: unknown,
    messageConstants: Record<string, string>
): unknown => {
    if (!messageJson) return messageJson;

    const source = JSON.stringify(messageJson);
    const replaced = Object.entries(messageConstants).reduce((current, [key, value]) => {
        if (!key) return current;
        return current.replace(new RegExp(escapeRegExp(key), 'g'), value);
    }, source);

    return safeJsonParse<unknown>(replaced, messageJson);
};

const createBlankRule = (): PolicyRuleDraft => ({
    initialMilestone: null,
    hasSecondaryCondition: false,
    secondaryMilestone: null,
    secondaryMilestoneCondition: null,
    delayDuration: 1,
    delayUnit: 'hours',
    messageTemplate: null,
    messageTemplateId: null,
    maxRetries: 1,
    retryFrequency: 'daily',
    messageConstants: { ...DEFAULT_MESSAGE_CONSTANTS },
    messageJson: null,
});

const normalizeDelayUnit = (value: unknown): DelayUnit => (value === 'days' ? 'days' : 'hours');

const normalizeRetryFrequency = (value: unknown): RetryFrequency => {
    if (value === 'hour' || value === 'weekly') return value;
    return 'daily';
};

const normalizeSecondaryCondition = (value: unknown): SecondaryCondition | null => {
    if (typeof value !== 'string') return null;

    const normalized = value.toLowerCase().trim().replace(/\s+/g, '_');
    if (normalized === 'reached' || normalized === 'not_reached') return normalized;
    return null;
};

const normalizeRule = (rule: any): PolicyRuleDraft => {
    const secondaryMilestone = rule?.secondaryMilestone ? String(rule.secondaryMilestone) : null;
    const secondaryMilestoneCondition = normalizeSecondaryCondition(rule?.secondaryConditionType);

    return {
        id: rule?.id,
        policyId: rule?.policyId,
        initialMilestone: rule?.initialMilestone ? String(rule.initialMilestone) : null,
        // `hasSecondaryCondition` is no longer returned by the API. A secondary
        // milestone may still be present for historical data, so the condition
        // type is the source of truth for whether this section is enabled.
        hasSecondaryCondition: Boolean(secondaryMilestoneCondition),
        secondaryMilestone,
        secondaryMilestoneCondition,
        delayDuration: Number(rule?.delayDuration) > 0 ? Number(rule.delayDuration) : 1,
        delayUnit: normalizeDelayUnit(rule?.delayUnit),
        messageTemplate: rule?.messageTemplate || null,
        messageTemplateId: rule?.messageTemplateId ? String(rule.messageTemplateId) : null,
        maxRetries: Number(rule?.maxRetries) > 0 ? Number(rule.maxRetries) : 1,
        retryFrequency: normalizeRetryFrequency(rule?.retryFrequency),
        messageConstants: normalizeVariableMap(rule?.messageConstants),
        messageJson: safeJsonParse<unknown>(rule?.messageJson, null),
    };
};

const normalizePolicy = (policy: RetargetingData | any): NormalizedPolicy => ({
    id: policy.id,
    name: policy.name || 'Untitled policy',
    isActive: normalizeBoolean(policy.isActive),
    isSelected: normalizeBoolean(policy.isSelected),
    botId: policy.botId || null,
    botName: policy.botName || null,
    botStatus: normalizeBoolean(policy.botStatus),
    clientId: policy.clientId,
    orgId: policy.orgId,
    createdAt: policy.createdAt,
    updatedAt: policy.updatedAt,
    rules: Array.isArray(policy.rules) ? policy.rules.map(normalizeRule) : [],
});

const extractTemplates = (response: any): WhatsAppTemplate[] => {
    return (
        response?.data?.resJson?.data ||
        response?.data?.res_json?.data ||
        response?.data?.data ||
        []
    );
};

const extractTemplateConstants = (response: any): { messageJson: unknown; variables: Record<string, string> } | null => {
    const payload = response?.data?.data || response?.data || response;
    const successful = payload?.status === undefined ? true : normalizeBoolean(payload.status);

    if (!successful) return null;

    const resJson = payload?.resJson || payload?.res_json;
    console.log(resJson);

    return {
        messageJson: resJson?.msgJson || resJson?.msg_json || null,
        variables: Object.fromEntries(
            Object.entries(normalizeVariableMap(resJson?.variables)).map(([key, value]) => [
                formatTemplateVariable(key),
                formatTemplateVariable(value),
            ])
        ),
    };
};

const getTemplateStatusColor = (status?: string): string => {
    if (!status) return 'default';
    if (status.toUpperCase() === 'APPROVED') return 'green';
    if (status.toUpperCase() === 'REJECTED') return 'red';
    return 'gold';
};

const buildClientData = (orgData: any): ClientData[] => {
    return (orgData?.wabaDetails || []).map((waba: any) => ({
        clientId: waba.clientId,
        clientSubdomain: waba.subdomain,
        clientWaRegisteredNumber: waba.wabaNumber,
        phoneNumerId: waba.phoneNumberId,
        wabaAccountId: waba.wabaId,
    }));
};

const buildRulesPayload = (rules: PolicyRuleDraft[]) => {
    return rules.map(rule => ({
        id: rule.id,
        policy_id: rule.policyId,
        initial_milestone: rule.initialMilestone,
        secondary_condition_type: rule.hasSecondaryCondition ? rule.secondaryMilestoneCondition : '',
        secondary_milestone: rule.hasSecondaryCondition ? rule.secondaryMilestone : '',
        delay_duration: rule.delayDuration,
        delay_unit: rule.delayUnit,
        message_template: rule.messageTemplate,
        message_template_id: rule.messageTemplateId,
        max_retries: rule.maxRetries,
        retry_frequency: rule.retryFrequency,
        message_constants: rule.messageConstants,
        message_json: applyTemplateVariables(rule.messageJson, rule.messageConstants),
    }));
};

const buildPolicyFromDraft = (
    client: ClientWithBot,
    id: PolicyId,
    name: string,
    rules: PolicyRuleDraft[],
    selectedOrgId?: string,
    isActive = false,
    isSelected = false
): NormalizedPolicy => ({
    id,
    name,
    isActive,
    isSelected,
    botId: client.bot?.id || null,
    botName: client.bot?.name || null,
    botStatus: Boolean(client.bot?.id),
    clientId: client.clientId,
    orgId: selectedOrgId,
    rules: rules.map(rule => ({ ...rule, messageConstants: { ...rule.messageConstants } })),
});

const MetaRetargeting: React.FC = () => {
    const { currentOrg, availableOrgs } = useAuthOrg();
    const { orgWabaData } = useMetaSetup();
    const { setBreadcrumbs } = useBreadcrumbs();

    const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(currentOrg?.orgId);
    const [clients, setClients] = useState<ClientWithBot[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [policySearchText, setPolicySearchText] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [editingPolicy, setEditingPolicy] = useState<NormalizedPolicy | null>(null);
    const [policyName, setPolicyName] = useState('');
    const [rules, setRules] = useState<PolicyRuleDraft[]>([createBlankRule()]);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [templateLoadingIndex, setTemplateLoadingIndex] = useState<number | null>(null);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewLoadingTemplateId, setPreviewLoadingTemplateId] = useState<string | null>(null);
    const [previewTemplateName, setPreviewTemplateName] = useState<string | null>(null);
    const [iframeSrc, setIframeSrc] = useState<string | null>(null);
    const [variableEditor, setVariableEditor] = useState<VariableEditorState>({
        open: false,
        ruleIndex: null,
        values: {},
    });

    useEffect(() => {
        setBreadcrumbs([{ title: <div className="text-lg">Retargeting</div> }]);
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (currentOrg?.orgId && !selectedOrgId) {
            setSelectedOrgId(currentOrg.orgId);
        }
    }, [currentOrg?.orgId, selectedOrgId]);

    useEffect(() => {
        setSelectedClientId(null);
        setPolicySearchText('');
    }, [selectedOrgId]);

    useEffect(() => {
        return () => {
            if (iframeSrc) {
                URL.revokeObjectURL(iframeSrc);
            }
        };
    }, [iframeSrc]);

    const selectedClient = useMemo(
        () => clients.find(client => client.clientId === selectedClientId) || null,
        [clients, selectedClientId]
    );

    const fetchClientBundle = useCallback(async (client: ClientData): Promise<ClientWithBot> => {
        try {
            const [botRes, milestoneRes, templateRes, policyRes] = await Promise.all([
                coreAPIService
                    .get<BotDetails>(CTW_NODE_API_ENDPOINT, `/common/v1/bot/${client.clientId}`)
                    .catch(() => null),
                coreAPIService
                    .get<BotMilestones>(CTW_NODE_API_ENDPOINT, `/common/v1/bot_milestone/${client.clientId}`)
                    .catch(() => ({ data: [] })),
                coreAPIService
                    .get<any>(CTW_NODE_API_ENDPOINT, `/common/v1/get_templates/${client.clientId}`)
                    .catch(() => ({ data: { resJson: { data: [] } } })),
                coreAPIService
                    .get<GetRetargetingResponse>(CTW_NODE_API_ENDPOINT, `/retargeting/v1/policies/${client.clientId}`)
                    .catch(() => ({ data: [] } as unknown as GetRetargetingResponse)),
            ]);

            const botData = botRes?.data;
            const policies = Array.isArray(policyRes?.data) ? policyRes.data.map(normalizePolicy) : [];

            return {
                ...client,
                bot: botData?.botId
                    ? {
                        id: botData.botId,
                        name: botData.botName,
                        isActive: normalizeBoolean(botData.status) || Boolean(botData.botId),
                    }
                    : null,
                milestones: (milestoneRes?.data || []).map(milestone => ({
                    id: String(milestone.milestoneId),
                    name: milestone.milestoneName,
                })),
                templates: extractTemplates(templateRes),
                policies,
            };
        } catch (error) {
            console.error(`Failed to load retargeting data for client ${client.clientId}`, error);
            return {
                ...client,
                bot: null,
                milestones: [],
                templates: [],
                policies: [],
            };
        }
    }, []);

    const loadClients = useCallback(async () => {
        if (!selectedOrgId || !orgWabaData) {
            setClients([]);
            return;
        }

        const orgData = orgWabaData.find(org => org.orgId === selectedOrgId);
        if (!orgData) {
            setClients([]);
            return;
        }

        const wabaClients = buildClientData(orgData);

        if (wabaClients.length === 0) {
            setClients([]);
            return;
        }

        setLoading(true);
        try {
            const clientsWithData = await Promise.all(wabaClients.map(fetchClientBundle));
            setClients(clientsWithData);
        } catch (error) {
            console.error('Failed to load retargeting clients', error);
            toast('error', 'Failed to load retargeting clients');
        } finally {
            setLoading(false);
        }
    }, [fetchClientBundle, orgWabaData, selectedOrgId]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!selectedOrgId || !orgWabaData) {
                if (!cancelled) setClients([]);
                return;
            }

            const orgData = orgWabaData.find(org => org.orgId === selectedOrgId);
            if (!orgData) {
                if (!cancelled) setClients([]);
                return;
            }

            const wabaClients = buildClientData(orgData);
            if (wabaClients.length === 0) {
                if (!cancelled) setClients([]);
                return;
            }

            setLoading(true);
            try {
                const clientsWithData = await Promise.all(wabaClients.map(fetchClientBundle));
                if (!cancelled) setClients(clientsWithData);
            } catch (error) {
                console.error('Failed to load retargeting clients', error);
                if (!cancelled) toast('error', 'Failed to load retargeting clients');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [fetchClientBundle, orgWabaData, selectedOrgId]);

    const refreshTemplates = useCallback(async (clientId: string) => {
        try {
            const templateRes = await coreAPIService.get<any>(
                CTW_NODE_API_ENDPOINT,
                `/common/v1/get_templates/${clientId}`
            );
            const templates = extractTemplates(templateRes);

            setClients(prev => prev.map(client => (
                client.clientId === clientId ? { ...client, templates } : client
            )));
        } catch (error) {
            console.error('Failed to refresh WhatsApp templates', error);
        }
    }, []);

    useEffect(() => {
        if (selectedClientId) {
            refreshTemplates(selectedClientId);
        }
    }, [refreshTemplates, selectedClientId]);

    const totals = useMemo(() => {
        return clients.reduce(
            (acc, client) => {
                acc.policies += client.policies.length;
                acc.activePolicies += client.policies.filter(policy => policy.isActive).length;
                acc.activeBots += client.bot?.id ? 1 : 0;
                return acc;
            },
            { policies: 0, activePolicies: 0, activeBots: 0 }
        );
    }, [clients]);

    const filteredPolicies = useMemo(() => {
        if (!selectedClient) return [];

        const search = policySearchText.trim().toLowerCase();
        if (!search) return selectedClient.policies;

        return selectedClient.policies.filter(policy => (
            policy.name.toLowerCase().includes(search) ||
            (policy.botName || '').toLowerCase().includes(search) ||
            policy.rules.some(rule => (rule.messageTemplate || '').toLowerCase().includes(search))
        ));
    }, [policySearchText, selectedClient]);

    const updateRule = (index: number, patch: Partial<PolicyRuleDraft>) => {
        setRules(prev => prev.map((rule, ruleIndex) => (
            ruleIndex === index ? { ...rule, ...patch } : rule
        )));
    };

    const openPolicyModal = (client: ClientWithBot, policy?: NormalizedPolicy) => {
        setSelectedClientId(client.clientId);

        if (policy) {
            setEditingPolicy(policy);
            setPolicyName(policy.name);
            setRules(policy.rules.length > 0 ? policy.rules.map(rule => ({
                ...rule,
                messageConstants: { ...rule.messageConstants },
            })) : [createBlankRule()]);
        } else {
            setEditingPolicy(null);
            setPolicyName('');
            setRules([createBlankRule()]);
        }

        setModalOpen(true);
    };

    const closePolicyModal = () => {
        setModalOpen(false);
        setEditingPolicy(null);
        setPolicyName('');
        setRules([createBlankRule()]);
        setVariableEditor({ open: false, ruleIndex: null, values: {} });
    };

    const handleTemplateSelect = async (templateId: string, index: number) => {
        if (!selectedClient) return;

        const selectedTemplate = selectedClient.templates.find(template => template.whatsappTemplateId === templateId);

        updateRule(index, {
            messageTemplateId: templateId,
            messageTemplate: selectedTemplate?.name || null,
            messageConstants: {},
            messageJson: null,
        });

        setTemplateLoadingIndex(index);
        try {
            const response = await coreAPIService.get<any>(
                CTW_NODE_API_ENDPOINT,
                `/common/v1/get_template_constants/${selectedClient.clientId}?template_id=${encodeURIComponent(templateId)}`
            );
            console.log(response);

            const constants = extractTemplateConstants(response);
            console.log(constants, "[]");


            if (!constants) {
                toast('error', 'Failed to load template variables');
                return;
            }

            updateRule(index, {
                messageJson: constants.messageJson,
                messageConstants: constants.variables,
            });
        } catch (error) {
            console.error('Failed to load template variables', error);
            toast('error', 'Failed to load template variables');
        } finally {
            setTemplateLoadingIndex(null);
        }
    };

    const previewTemplate = async (templateId: string | null) => {
        if (!selectedClient || !templateId) return;

        const selectedTemplate = selectedClient.templates.find(template => template.whatsappTemplateId === templateId);

        setPreviewLoadingTemplateId(templateId);
        try {
            const response = await coreAPIService.get<any>(
                CTW_NODE_API_ENDPOINT,
                `/common/v1/get_template_preview/${selectedClient.clientId}?template_id=${encodeURIComponent(templateId)}`
            );
            const html = typeof response === 'string' ? response : response?.data || '';
            const nextUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));

            setIframeSrc(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return nextUrl;
            });
            setPreviewTemplateName(selectedTemplate?.name || 'WhatsApp template');
            setPreviewModalOpen(true);
        } catch (error) {
            console.error('Failed to load template preview', error);
            toast('error', 'Failed to load preview');
        } finally {
            setPreviewLoadingTemplateId(null);
        }
    };

    const validatePolicy = (client: ClientWithBot): string | null => {
        if (!client.bot?.id) return 'A bot is required before creating retargeting policies';
        if (!policyName.trim()) return 'Policy name is required';

        for (let index = 0; index < rules.length; index += 1) {
            const rule = rules[index];
            const ruleNumber = index + 1;

            if (!rule.initialMilestone) return `Rule ${ruleNumber}: initial milestone is required`;
            if (rule.hasSecondaryCondition && !rule.secondaryMilestoneCondition) {
                return `Rule ${ruleNumber}: secondary condition is required`;
            }
            if (rule.hasSecondaryCondition && !rule.secondaryMilestone) {
                return `Rule ${ruleNumber}: secondary milestone is required`;
            }
            if (!rule.messageTemplateId) return `Rule ${ruleNumber}: WhatsApp template is required`;
            if (!rule.delayDuration || rule.delayDuration < 1) return `Rule ${ruleNumber}: wait duration must be at least 1`;
            if (!rule.maxRetries || rule.maxRetries < 1) return `Rule ${ruleNumber}: retries must be at least 1`;
        }

        return null;
    };

    const savePolicy = async () => {
        if (!selectedClient) return;

        const validationError = validatePolicy(selectedClient);
        if (validationError) {
            toast('error', validationError);
            return;
        }

        const trimmedPolicyName = policyName.trim();
        const payload = {
            name: trimmedPolicyName,
            bot_id: selectedClient.bot?.id,
            bot_name: selectedClient.bot?.name,
            bot_status: Boolean(selectedClient.bot?.id),
            client_id: selectedClient.clientId,
            org_id: selectedOrgId,
            rules: buildRulesPayload(rules),
        };

        setSubmitLoading(true);
        try {
            const endpoint = editingPolicy
                ? `/retargeting/v1/policies/${editingPolicy.id}`
                : '/retargeting/v1/policies';
            const response = editingPolicy
                ? await coreAPIService.put<any>(CTW_NODE_API_ENDPOINT, endpoint, payload)
                : await coreAPIService.post<any>(CTW_NODE_API_ENDPOINT, endpoint, payload);

            const responsePolicy = response?.data?.id ? normalizePolicy(response.data) : null;
            const nextPolicy = responsePolicy || buildPolicyFromDraft(
                selectedClient,
                editingPolicy?.id || response?.data?.id || Date.now(),
                trimmedPolicyName,
                rules,
                selectedOrgId,
                editingPolicy?.isActive || false,
                editingPolicy?.isSelected || false
            );

            setClients(prev => prev.map(client => {
                if (client.clientId !== selectedClient.clientId) return client;

                const policyExists = client.policies.some(policy => policy.id === nextPolicy.id);
                const policies = policyExists
                    ? client.policies.map(policy => (policy.id === nextPolicy.id ? nextPolicy : policy))
                    : [...client.policies, nextPolicy];

                return { ...client, policies };
            }));

            toast('success', `Policy ${editingPolicy ? 'updated' : 'created'}`);
            closePolicyModal();
        } catch (error) {
            console.error('Failed to save retargeting policy', error);
            toast('error', 'Failed to save policy');
        } finally {
            setSubmitLoading(false);
        }
    };

    const toggleActive = async (policy: NormalizedPolicy, checked: boolean) => {
        if (!selectedClient) return;

        const payload = {
            name: policy.name,
            bot_id: selectedClient.bot?.id || policy.botId,
            bot_name: selectedClient.bot?.name || policy.botName,
            bot_status: Boolean(selectedClient.bot?.id || policy.botStatus),
            client_id: selectedClient.clientId,
            org_id: selectedOrgId || policy.orgId,
            is_active: checked,
            is_selected: checked,
            rules: buildRulesPayload(policy.rules),
        };

        try {
            await coreAPIService.put(CTW_NODE_API_ENDPOINT, `/retargeting/v1/policies/${policy.id}`, payload);

            setClients(prev => prev.map(client => {
                if (client.clientId !== selectedClient.clientId) return client;

                return {
                    ...client,
                    policies: client.policies.map(item => (
                        item.id === policy.id
                            ? { ...item, isActive: checked, isSelected: checked }
                            : { ...item, isActive: false, isSelected: false }
                    )),
                };
            }));

            toast('success', `Policy ${checked ? 'activated' : 'deactivated'}`);
        } catch (error) {
            console.error('Failed to update retargeting policy status', error);
            toast('error', 'Failed to update policy');
        }
    };

    const overviewRows = useMemo(() => [
        {
            key: 'numbers',
            metric: 'WhatsApp Numbers',
            value: clients.length.toLocaleString(),
            context: 'WABA clients available for this account',
        },
        {
            key: 'bots',
            metric: 'Linked Bots',
            value: totals.activeBots.toLocaleString(),
            context: 'Numbers ready for retargeting policy creation',
        },
        {
            key: 'policies',
            metric: 'Active Policies',
            value: `${totals.activePolicies}/${totals.policies}`,
            context: totals.policies > 0 ? 'Enabled policies across all numbers' : 'No policies configured yet',
        },
    ], [clients.length, totals.activeBots, totals.activePolicies, totals.policies]);

    const overviewColumns = [
        {
            title: 'Metric',
            dataIndex: 'metric',
            key: 'metric',
            render: (metric: string) => <Text strong>{metric}</Text>,
        },
        {
            title: 'Count',
            dataIndex: 'value',
            key: 'value',
            align: 'right' as const,
            render: (value: string) => <Text className="text-xl font-semibold">{value}</Text>,
        },
        {
            title: 'Context',
            dataIndex: 'context',
            key: 'context',
            render: (context: string) => <Text type="secondary">{context}</Text>,
        },
    ];

    const policyColumns = [
        {
            title: 'Policy',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: NormalizedPolicy) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    <Text type="secondary" className="text-xs">
                        {record.rules.length} rule{record.rules.length === 1 ? '' : 's'}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Bot',
            key: 'bot',
            render: (_: unknown, record: NormalizedPolicy) => {
                const hasBot = Boolean(selectedClient?.bot?.id || record.botId);
                return (
                    <Space>
                        <Text>{selectedClient?.bot?.name || record.botName || 'No bot'}</Text>
                        <Tag color={hasBot ? 'green' : 'red'}>{hasBot ? 'Available' : 'Missing'}</Tag>
                    </Space>
                );
            },
        },
        {
            title: 'Templates',
            key: 'templates',
            render: (_: unknown, record: NormalizedPolicy) => (
                <Space wrap size={[4, 4]}>
                    {record.rules.slice(0, 2).map((rule, index) => (
                        <Tag key={`${record.id}-${rule.messageTemplateId || index}`}>
                            {rule.messageTemplate || 'No template'}
                        </Tag>
                    ))}
                    {record.rules.length > 2 && <Tag>+{record.rules.length - 2}</Tag>}
                </Space>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: unknown, record: NormalizedPolicy) => (
                <Switch
                    checked={record.isActive}
                    disabled={!selectedClient?.bot?.id}
                    onChange={checked => toggleActive(record, checked)}
                />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, record: NormalizedPolicy) => (
                <Space>
                    <Tooltip title="Preview details">
                        <Button size="small" icon={<EyeOutlined />} onClick={() => selectedClient && openPolicyModal(selectedClient, record)} />
                    </Tooltip>
                    <Tooltip title={selectedClient?.bot?.id ? 'Edit policy' : 'Bot required'}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<EditOutlined />}
                            disabled={!selectedClient?.bot?.id}
                            onClick={() => selectedClient && openPolicyModal(selectedClient, record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const clientColumns = [
        {
            title: 'WhatsApp Number',
            dataIndex: 'clientWaRegisteredNumber',
            key: 'number',
            render: (number: string, client: ClientWithBot) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{number || 'N/A'}</Text>
                    <Text type="secondary" className="text-xs">{client.clientSubdomain}</Text>
                </Space>
            ),
        },
        {
            title: 'Bot',
            key: 'bot',
            render: (_: unknown, client: ClientWithBot) => {
                const hasBot = Boolean(client.bot?.id);

                return (
                    <Space direction="vertical" size={0}>
                        <Space size="small" wrap>
                            <Text>{client.bot?.name || 'No bot linked'}</Text>
                            <Tag color={hasBot ? 'green' : 'red'}>{hasBot ? 'Linked' : 'Missing'}</Tag>
                        </Space>
                        <Text type="secondary" className="text-xs">
                            {hasBot ? 'Ready for retargeting' : 'Policy creation disabled'}
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: 'Policies',
            key: 'policies',
            render: (_: unknown, client: ClientWithBot) => {
                const activePolicyCount = client.policies.filter(policy => policy.isActive).length;

                return (
                    <Space size="small" wrap>
                        <Tag color={activePolicyCount > 0 ? 'green' : 'default'}>
                            {activePolicyCount} active
                        </Tag>
                        <Text type="secondary">{client.policies.length} total</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Templates',
            key: 'templates',
            render: (_: unknown, client: ClientWithBot) => (
                <Space direction="vertical" size={0}>
                    <Text>{client.templates.length.toLocaleString()}</Text>
                    <Text type="secondary" className="text-xs">WhatsApp templates</Text>
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, client: ClientWithBot) => {
                const hasBot = Boolean(client.bot?.id);

                return (
                    <Space wrap>
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => setSelectedClientId(client.clientId)}
                        >
                            View policies
                        </Button>
                        <Tooltip title={hasBot ? '' : 'Bot must be linked before creating policies'}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={!hasBot}
                                onClick={() => openPolicyModal(client)}
                            >
                                Create
                            </Button>
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    return (
        <CommonLayout>
            <div className="space-y-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <Title level={3} className="!mb-1">Retargeting</Title>
                        <Text type="secondary">Manage automated WhatsApp follow-up policies by WABA client.</Text>
                    </div>
                    <Space wrap>
                        <Button icon={<ReloadOutlined />} onClick={loadClients} loading={loading}>
                            Refresh
                        </Button>
                        <Select
                            style={{ width: 260 }}
                            value={selectedOrgId}
                            onChange={setSelectedOrgId}
                            placeholder="Select account"
                            options={availableOrgs?.map(org => ({ label: org.orgName, value: org.orgId }))}
                        />
                    </Space>
                </div>

                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <Table
                        columns={overviewColumns}
                        dataSource={overviewRows}
                        rowKey="key"
                        pagination={false}
                        size="middle"
                    />
                </div>

                {!selectedClient ? (
                    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                        <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
                            <div>
                                <Text strong>Available numbers</Text>
                                <div>
                                    <Text type="secondary">Policy readiness by WABA number.</Text>
                                </div>
                            </div>
                            <Tag color="blue">{clients.length} total</Tag>
                        </div>
                        <Table
                            columns={clientColumns}
                            dataSource={clients}
                            rowKey="clientId"
                            loading={loading}
                            pagination={{
                                pageSize: CLIENT_PAGE_SIZE,
                                showSizeChanger: false,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} numbers`,
                            }}
                            locale={{ emptyText: <Empty description="No WhatsApp clients found for this account" /> }}
                            scroll={{ x: 900 }}
                        />
                    </div>
                ) : (
                    <div className="space-y-4 rounded-lg bg-gray-50 p-4">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <Space direction="vertical" size="small">
                                <Button
                                    type="link"
                                    className="!h-auto !p-0"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => setSelectedClientId(null)}
                                >
                                    Back to clients
                                </Button>
                                <Title level={4} className="!mb-0">{selectedClient.clientSubdomain}</Title>
                                <Space wrap>
                                    <Tag color={selectedClient.bot?.id ? 'green' : 'red'}>
                                        {selectedClient.bot?.name || 'No bot linked'}
                                    </Tag>
                                    <Text type="secondary">{selectedClient.clientWaRegisteredNumber}</Text>
                                </Space>
                            </Space>
                            <Space wrap>
                                <Input.Search
                                    allowClear
                                    placeholder="Search policies"
                                    value={policySearchText}
                                    onChange={event => setPolicySearchText(event.target.value)}
                                    style={{ width: 260 }}
                                />
                                <Tooltip title={selectedClient.bot?.id ? '' : 'Bot must be linked before creating policies'}>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        disabled={!selectedClient.bot?.id}
                                        onClick={() => openPolicyModal(selectedClient)}
                                    >
                                        Create policy
                                    </Button>
                                </Tooltip>
                            </Space>
                        </div>

                        {!selectedClient.bot?.id && (
                            <Alert
                                type="warning"
                                showIcon
                                message="This client does not have a linked bot. Policies can be viewed, but creation and activation are disabled."
                            />
                        )}

                        <Table
                            columns={policyColumns}
                            dataSource={filteredPolicies}
                            rowKey="id"
                            pagination={{ pageSize: POLICY_PAGE_SIZE }}
                            locale={{ emptyText: <Empty description="No retargeting policies found" /> }}
                        />
                    </div>
                )}
            </div>

            <Modal
                title={(
                    <Space direction="vertical" size={0}>
                        <Title level={4} className="!mb-0">
                            {editingPolicy ? 'Edit Retargeting Policy' : 'Create Retargeting Policy'}
                        </Title>
                        <Text type="secondary" className="text-sm">
                            Audience, schedule, and template configuration
                        </Text>
                    </Space>
                )}
                open={modalOpen}
                onCancel={closePolicyModal}
                width={1120}
                zIndex={1000}
                destroyOnClose
                styles={{ body: { paddingTop: 16 } }}
                footer={[
                    <Button key="cancel" onClick={closePolicyModal}>Cancel</Button>,
                    <Button key="save" type="primary" loading={submitLoading} onClick={savePolicy}>
                        {editingPolicy ? 'Update policy' : 'Create policy'}
                    </Button>,
                ]}
            >
                <Space direction="vertical" className="w-full" size="large">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px] lg:items-end">
                            <div>
                                <Text strong>Policy name</Text>
                                <Input
                                    className="mt-2"
                                    size="large"
                                    value={policyName}
                                    onChange={event => setPolicyName(event.target.value)}
                                    placeholder="Example: Cart abandonment follow-up"
                                />
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white p-3">
                                <Text type="secondary" className="text-xs uppercase tracking-wide">Linked bot</Text>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <Tag color={selectedClient?.bot?.id ? 'green' : 'red'}>
                                        {selectedClient?.bot?.name || 'No bot'}
                                    </Tag>
                                    <Text type="secondary" className="text-xs">
                                        {selectedClient?.clientWaRegisteredNumber || 'No number selected'}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                            <Text strong>Rules</Text>
                            <div>
                                <Text type="secondary">{rules.length} configured step{rules.length === 1 ? '' : 's'}</Text>
                            </div>
                        </div>
                        <Button type="dashed" icon={<PlusOutlined />} onClick={() => setRules(prev => [...prev, createBlankRule()])}>
                            Add rule
                        </Button>
                    </div>

                    {rules.map((rule, index) => {
                        const milestoneOptions = selectedClient?.milestones.map(milestone => ({
                            label: milestone.name,
                            value: milestone.id,
                        })) || [];
                        const templateOptions = selectedClient?.templates.map(template => ({
                            label: (
                                <Space className="w-full justify-between">
                                    <span>{template.name}</span>
                                    <Tag color={getTemplateStatusColor(template.status)}>{template.status || 'UNKNOWN'}</Tag>
                                </Space>
                            ),
                            value: template.whatsappTemplateId,
                            disabled: template.status ? template.status.toUpperCase() !== 'APPROVED' : false,
                        })) || [];

                        return (
                            <div
                                key={`${rule.id || 'new'}-${index}`}
                                className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
                            >
                                <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                                    <Space wrap>
                                        <Tag color="blue">Rule {index + 1}</Tag>
                                        <Text strong>{rule.messageTemplate || 'Choose a message template'}</Text>
                                    </Space>
                                    {rules.length > 1 && (
                                        <Tooltip title="Remove rule">
                                            <Button
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => setRules(prev => prev.filter((_, ruleIndex) => ruleIndex !== index))}
                                            />
                                        </Tooltip>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
                                    <div className="rounded-lg border border-gray-100 p-4">
                                        <Text strong>Audience</Text>
                                        <div className="mt-3">
                                            <Text type="secondary">Initial milestone</Text>
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                className="mt-2 w-full"
                                                value={rule.initialMilestone || undefined}
                                                placeholder="Select milestone"
                                                options={milestoneOptions}
                                                onChange={value => updateRule(index, { initialMilestone: value })}
                                            />
                                        </div>
                                        <div className="mt-4 flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-3 py-2">
                                            <Space direction="vertical" size={0}>
                                                <Text>Secondary condition</Text>
                                                <Text type="secondary" className="text-xs">Optional milestone filter</Text>
                                            </Space>
                                            <Switch
                                                checked={rule.hasSecondaryCondition}
                                                onChange={checked => updateRule(index, {
                                                    hasSecondaryCondition: checked,
                                                    secondaryMilestone: checked ? rule.secondaryMilestone : null,
                                                    secondaryMilestoneCondition: checked ? rule.secondaryMilestoneCondition : null,
                                                })}
                                            />
                                        </div>

                                        {rule.hasSecondaryCondition && (
                                            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[160px_1fr]">
                                                <div>
                                                    <Text type="secondary">Condition</Text>
                                                    <Select
                                                        className="mt-2 w-full"
                                                        value={rule.secondaryMilestoneCondition || undefined}
                                                        placeholder="Condition"
                                                        options={secondaryConditionOptions}
                                                        onChange={value => updateRule(index, { secondaryMilestoneCondition: value })}
                                                    />
                                                </div>
                                                <div>
                                                    <Text type="secondary">Secondary milestone</Text>
                                                    <Select
                                                        showSearch
                                                        optionFilterProp="label"
                                                        className="mt-2 w-full"
                                                        value={rule.secondaryMilestone || undefined}
                                                        placeholder="Select milestone"
                                                        options={milestoneOptions}
                                                        onChange={value => updateRule(index, { secondaryMilestone: value })}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="rounded-lg border border-gray-100 p-4">
                                        <Text strong>Schedule and retry</Text>
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px]">
                                            <div>
                                                <Text type="secondary">Wait duration</Text>
                                                <InputNumber
                                                    className="mt-2 w-full"
                                                    min={1}
                                                    value={rule.delayDuration}
                                                    onChange={value => updateRule(index, { delayDuration: Number(value) || 1 })}
                                                />
                                            </div>
                                            <div>
                                                <Text type="secondary">Unit</Text>
                                                <Select
                                                    className="mt-2 w-full"
                                                    value={rule.delayUnit}
                                                    options={delayUnitOptions}
                                                    onChange={value => updateRule(index, { delayUnit: value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
                                            <div>
                                                <Text type="secondary">Max retries</Text>
                                                <InputNumber
                                                    className="mt-2 w-full"
                                                    min={1}
                                                    max={10}
                                                    value={rule.maxRetries}
                                                    onChange={value => updateRule(index, { maxRetries: Number(value) || 1 })}
                                                />
                                            </div>
                                            <div>
                                                <Text type="secondary">Frequency</Text>
                                                <Select
                                                    className="mt-2 w-full"
                                                    value={rule.retryFrequency}
                                                    options={retryFrequencyOptions}
                                                    onChange={value => updateRule(index, { retryFrequency: value })}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-lg border border-gray-100 p-4 lg:col-span-2">
                                        <Text strong>Message template</Text>
                                        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto]">
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                value={rule.messageTemplateId || undefined}
                                                placeholder="Select an approved WhatsApp template"
                                                options={templateOptions}
                                                loading={templateLoadingIndex === index}
                                                onChange={value => handleTemplateSelect(value, index)}
                                            />
                                            <Space wrap>
                                                <Button
                                                    icon={<EyeOutlined />}
                                                    disabled={!rule.messageTemplateId}
                                                    loading={previewLoadingTemplateId === rule.messageTemplateId}
                                                    onClick={() => previewTemplate(rule.messageTemplateId)}
                                                >
                                                    Preview
                                                </Button>
                                                <Button
                                                    disabled={!rule.messageTemplateId}
                                                    onClick={() => setVariableEditor({
                                                        open: true,
                                                        ruleIndex: index,
                                                        values: { ...rule.messageConstants },
                                                    })}
                                                >
                                                    Variables
                                                </Button>
                                            </Space>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Space>
            </Modal>

            <Modal
                title="Set Template Variables"
                open={variableEditor.open}
                zIndex={1100}
                onCancel={() => setVariableEditor({ open: false, ruleIndex: null, values: {} })}
                onOk={() => {
                    if (variableEditor.ruleIndex !== null) {
                        updateRule(variableEditor.ruleIndex, { messageConstants: variableEditor.values });
                    }
                    setVariableEditor({ open: false, ruleIndex: null, values: {} });
                }}
            >
                {Object.keys(variableEditor.values).length === 0 ? (
                    <Empty description="This template has no editable variables" />
                ) : (
                    <Space direction="vertical" className="w-full">
                        {Object.entries(variableEditor.values).map(([key, value]) => (
                            <div key={key}>
                                <Text>{key}</Text>
                                <Input
                                    className="mt-1"
                                    value={value}
                                    onChange={event => setVariableEditor(prev => ({
                                        ...prev,
                                        values: { ...prev.values, [key]: event.target.value },
                                    }))}
                                />
                            </div>
                        ))}
                    </Space>
                )}
            </Modal>

            <Modal
                title={(
                    <Space direction="vertical" size={0}>
                        <Title level={4} className="!mb-0">Template Preview</Title>
                        <Text type="secondary" className="text-sm">
                            {previewTemplateName || 'WhatsApp template'}
                        </Text>
                    </Space>
                )}
                open={previewModalOpen}
                zIndex={1100}
                onCancel={() => setPreviewModalOpen(false)}
                width={760}
                footer={[
                    <Button key="close" onClick={() => setPreviewModalOpen(false)}>
                        Close
                    </Button>,
                ]}
            >
                {iframeSrc ? (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <Space wrap>
                                <Tag color="green">Preview</Tag>
                                <Text strong>{previewTemplateName || 'WhatsApp template'}</Text>
                            </Space>
                            <Text type="secondary" className="text-xs">
                                {selectedClient?.clientWaRegisteredNumber || 'Selected number'}
                            </Text>
                        </div>
                        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                            <iframe
                                title="Template Preview"
                                src={iframeSrc}
                                style={{ width: '100%', height: 560, border: 'none', display: 'block' }}
                            />
                        </div>
                    </div>
                ) : (
                    <Empty description="No template preview loaded" />
                )}
            </Modal>
        </CommonLayout>
    );
};

export default MetaRetargeting;
